import React from 'react';
import { Layout } from '../components/layout/Layout';
import { SeoHead } from '../components/ui/SeoHead';
import { Section } from '../components/ui/Section';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { PageNavigator } from '../components/ui/PageNavigator';
import { Shield, ShieldCheck, AlertTriangle, CheckCircle, FileText, HardHat, Activity, Eye, BookOpen, Settings, ClipboardCheck, BarChart, Users, ArrowRight, TrendingUp, Award, Clock, Wrench } from 'lucide-react';
import { VisualBreak } from '../components/ui/VisualBreak';
import { KPIStrip } from '../components/ui/KPIStrip';
import { PowerStatement } from '../components/ui/PowerStatement';

export const Safety = () => {
  const sections = [
    { id: 'hse-snapshot', label: 'Performance Snapshot' },
    { id: 'hse-dashboard', label: 'HSE Indicators' },
    { id: 'ims', label: 'IMS & Accountability' },
    { id: 'field-safety', label: 'Field Safety Execution' },
    { id: 'incident-management', label: 'Incident Management' },
    { id: 'certification', label: 'Certification Status' },
    { id: 'training', label: 'Training & Competency' },
    { id: 'equipment-integrity', label: 'Equipment Integrity' },
    { id: 'compliance', label: 'Compliance & Audits' },
  ];

  return (
    <Layout>
      <SeoHead
        title="HSE Performance & Compliance | TRIR, LTIR, EMR Data"
        description="EnergiPro Group HSE performance: TRIR 0.00, LTIR 0.00, 185K+ safe manhours. ISO 45001:2018 certified. 100% JSA compliance, 12 stop-work exercises, 218 safety observations."
        canonicalPath="/safety"
      />
      {/* HERO */}
      <div className="bg-slate-900 pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-6">
            <Shield className="text-orange-600" size={48} />
            <h1 className="text-4xl md:text-5xl font-bold text-white">HSE PERFORMANCE & COMPLIANCE</h1>
          </div>
          <p className="text-xl text-slate-300 max-w-4xl leading-relaxed mb-6">
            Measurable HSE performance. Auditable systems. Risk control documented at every stage of execution.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button href="/request-quote" variant="primary" className="px-8 py-3">
              REQUEST HSE DATA PACKAGE
            </Button>
            <Button href="/files/energipro-capability-statement.pdf" variant="outline-light" className="px-8 py-3">
              DOWNLOAD CAPABILITY STATEMENT
            </Button>
          </div>
        </div>
      </div>

      {/* Certification Badge Strip */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Certified</span>
            <div className="flex items-center gap-2">
              <ShieldCheck className="text-green-600" size={20} />
              <span className="text-sm font-bold text-slate-900">ISO 9001:2015</span>
              <span className="text-xs text-slate-500">Quality</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="text-green-600" size={20} />
              <span className="text-sm font-bold text-slate-900">ISO 14001:2015</span>
              <span className="text-xs text-slate-500">Environment</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="text-green-600" size={20} />
              <span className="text-sm font-bold text-slate-900">ISO 45001:2018</span>
              <span className="text-xs text-slate-500">OHS</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="text-slate-600" size={20} />
              <span className="text-sm font-bold text-slate-900">API Q1/Q2</span>
              <span className="text-xs text-slate-500">Aligned</span>
            </div>
          </div>
        </div>
      </div>

      <PageNavigator sections={sections} />

      {/* 1. HSE PERFORMANCE SNAPSHOT (Last 12 Months) — NEW */}
      <Section id="hse-snapshot" background="steel">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-2">HSE PERFORMANCE SNAPSHOT (LAST 12 MONTHS)</h2>
          <p className="text-slate-600">Summary metrics from field operations across KSA, UAE, and Egypt. Period: Q2 2025 – Q1 2026.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white border border-slate-200 p-6 text-center rounded-sm shadow-sm border-t-4 border-t-green-600">
            <span className="block text-4xl font-bold text-slate-900 mb-1">0.00</span>
            <span className="block text-sm text-slate-600 font-medium uppercase tracking-wide">TRIR</span>
            <span className="text-xs text-slate-500 mt-2 block">Total Recordable Incident Rate</span>
          </div>
          <div className="bg-white border border-slate-200 p-6 text-center rounded-sm shadow-sm border-t-4 border-t-green-600">
            <span className="block text-4xl font-bold text-slate-900 mb-1">0.00</span>
            <span className="block text-sm text-slate-600 font-medium uppercase tracking-wide">LTIR</span>
            <span className="text-xs text-slate-500 mt-2 block">Lost Time Incident Rate</span>
          </div>
          <div className="bg-white border border-slate-200 p-6 text-center rounded-sm shadow-sm border-t-4 border-t-orange-600">
            <span className="block text-4xl font-bold text-slate-900 mb-1">185,000+</span>
            <span className="block text-sm text-slate-600 font-medium uppercase tracking-wide">Man-Hours</span>
            <span className="text-xs text-slate-500 mt-2 block">Total exposure hours worked</span>
          </div>
          <div className="bg-white border border-slate-200 p-6 text-center rounded-sm shadow-sm border-t-4 border-t-orange-600">
            <span className="block text-4xl font-bold text-slate-900 mb-1">47</span>
            <span className="block text-sm text-slate-600 font-medium uppercase tracking-wide">Near-Miss Reports</span>
            <span className="text-xs text-slate-500 mt-2 block">Proactive reporting — all investigated & closed</span>
          </div>
        </div>

        <div className="mt-6 bg-white border border-slate-200 p-4 rounded-sm">
          <p className="text-sm text-slate-600">
            Detailed monthly breakdown, EMR data, and per-project HSE statistics available as part of vendor prequalification package.
          </p>
        </div>
      </Section>

      {/* 2. HSE PERFORMANCE INDICATORS (Expanded) */}
      <Section id="hse-dashboard">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <BarChart className="text-orange-600" size={32} />
            <h2 className="text-3xl font-bold text-slate-900">HSE PERFORMANCE INDICATORS</h2>
          </div>
          <p className="text-slate-600 max-w-3xl">
            Tracked monthly. Reported to executive leadership. Client-ready data maintained in standard OSHA 300 format.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white border border-slate-200 p-6 text-center rounded-sm shadow-sm">
            <div className="bg-green-50 w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full">
              <TrendingUp className="text-green-700" size={28} />
            </div>
            <span className="block text-sm text-slate-500 font-medium mb-2 uppercase tracking-wide">TRIR</span>
            <span className="block text-3xl font-bold text-slate-900 mb-1">0.00</span>
            <span className="text-xs text-slate-500">Based on 200,000-hour normalisation</span>
          </div>
          <div className="bg-white border border-slate-200 p-6 text-center rounded-sm shadow-sm">
            <div className="bg-green-50 w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full">
              <Activity className="text-green-700" size={28} />
            </div>
            <span className="block text-sm text-slate-500 font-medium mb-2 uppercase tracking-wide">LTIR</span>
            <span className="block text-3xl font-bold text-slate-900 mb-1">0.00</span>
            <span className="text-xs text-slate-500">Zero lost-time incidents recorded</span>
          </div>
          <div className="bg-white border border-slate-200 p-6 text-center rounded-sm shadow-sm">
            <div className="bg-green-50 w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full">
              <Award className="text-green-700" size={28} />
            </div>
            <span className="block text-sm text-slate-500 font-medium mb-2 uppercase tracking-wide">EMR</span>
            <span className="block text-3xl font-bold text-slate-900 mb-1">0.85</span>
            <span className="text-xs text-slate-500">Experience Modification Rate</span>
          </div>
          <div className="bg-white border border-slate-200 p-6 text-center rounded-sm shadow-sm">
            <div className="bg-green-50 w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full">
              <Clock className="text-green-700" size={28} />
            </div>
            <span className="block text-sm text-slate-500 font-medium mb-2 uppercase tracking-wide">Man-Hours (12-Month)</span>
            <span className="block text-3xl font-bold text-slate-900 mb-1">185K+</span>
            <span className="text-xs text-slate-500">Cumulative field exposure hours</span>
          </div>
        </div>
      </Section>

      {/* 3. IMS — Accountability-Focused */}
      <Section id="ims" background="light">
        <div className="flex flex-col lg:flex-row gap-12">
          <div className="flex-1">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">INTEGRATED MANAGEMENT SYSTEM (IMS)</h2>
            <p className="text-slate-600 mb-6 leading-relaxed">
              IMS is implemented, certified, and operational. Governs all field and workshop activities. Third-party certified to ISO 9001:2015, ISO 14001:2015, and ISO 45001:2018. Accountability is assigned — not implied.
            </p>

            {/* Accountability Structure */}
            <div className="mb-8">
              <h3 className="font-bold text-slate-900 mb-3 text-lg">Accountability Structure</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="bg-orange-100 p-1.5 rounded-full mt-0.5"><Users size={14} className="text-orange-700" /></div>
                  <div>
                    <span className="font-semibold text-slate-900">CEO — HSE Performance Owner</span>
                    <p className="text-sm text-slate-600">Accountable for company TRIR, policy enforcement, and resource allocation for HSE system maintenance.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-orange-100 p-1.5 rounded-full mt-0.5"><Shield size={14} className="text-orange-700" /></div>
                  <div>
                    <span className="font-semibold text-slate-900">Operations Director — Audit Outcome Owner</span>
                    <p className="text-sm text-slate-600">Owns corrective action closure. Signs off on internal audit findings. Escalation authority for non-conformances.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-orange-100 p-1.5 rounded-full mt-0.5"><ClipboardCheck size={14} className="text-orange-700" /></div>
                  <div>
                    <span className="font-semibold text-slate-900">HSE Manager — Field Execution Authority</span>
                    <p className="text-sm text-slate-600">Stop-work authority. Daily compliance verification. Incident investigation lead. Reports directly to CEO.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-orange-100 p-1.5 rounded-full mt-0.5"><FileText size={14} className="text-orange-700" /></div>
                  <div>
                    <span className="font-semibold text-slate-900">QA Lead — Document Control & Audit Coordination</span>
                    <p className="text-sm text-slate-600">Maintains IMS documentation. Tracks NCRs. Coordinates internal/external audit schedules.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Audit Cycle */}
            <div className="mb-8">
              <h3 className="font-bold text-slate-900 mb-3 text-lg">Audit Cycle</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white p-4 border border-slate-200 rounded-sm">
                  <span className="block font-bold text-slate-900 text-sm">Internal Audits</span>
                  <span className="text-sm text-slate-600">Quarterly — all operational areas. Findings tracked to closure.</span>
                </div>
                <div className="bg-white p-4 border border-slate-200 rounded-sm">
                  <span className="block font-bold text-slate-900 text-sm">Management Reviews</span>
                  <span className="text-sm text-slate-600">Bi-annual — CEO + Operations Director. KPIs, NCRs, resource decisions.</span>
                </div>
                <div className="bg-white p-4 border border-slate-200 rounded-sm">
                  <span className="block font-bold text-slate-900 text-sm">External Audits</span>
                  <span className="text-sm text-slate-600">Annual third-party assessment (scheduled). Client audit support on-demand.</span>
                </div>
                <div className="bg-white p-4 border border-slate-200 rounded-sm">
                  <span className="block font-bold text-slate-900 text-sm">NCR Close-out Rate</span>
                  <span className="text-sm text-slate-600">Target: 100% within 30 days. Current: 96% on-time closure.</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1">
            {/* PDCA Diagram */}
            <div className="bg-white p-8 border border-slate-200 rounded-sm mb-6">
              <h3 className="font-bold text-slate-900 mb-6 text-center text-lg">IMS Continuous Improvement Cycle</h3>
              <div className="flex flex-col items-center gap-0">
                <div className="bg-slate-900 text-white px-6 py-3 rounded-sm font-bold text-center w-full max-w-[240px]">POLICY</div>
                <ArrowRight className="text-orange-600 rotate-90 my-2" size={24} />
                <div className="bg-orange-600 text-white px-6 py-3 rounded-sm font-bold text-center w-full max-w-[240px]">PLANNING</div>
                <ArrowRight className="text-orange-600 rotate-90 my-2" size={24} />
                <div className="bg-slate-700 text-white px-6 py-3 rounded-sm font-bold text-center w-full max-w-[240px]">EXECUTION</div>
                <ArrowRight className="text-orange-600 rotate-90 my-2" size={24} />
                <div className="bg-slate-600 text-white px-6 py-3 rounded-sm font-bold text-center w-full max-w-[240px]">MONITORING</div>
                <ArrowRight className="text-orange-600 rotate-90 my-2" size={24} />
                <div className="bg-green-700 text-white px-6 py-3 rounded-sm font-bold text-center w-full max-w-[240px]">IMPROVEMENT</div>
              </div>
              <p className="text-xs text-slate-500 text-center mt-4 italic">Plan–Do–Check–Act applied across all IMS functions</p>
            </div>

            {/* Standards — Certified */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-green-50 p-4 border-l-4 border-green-600">
                <span className="block font-bold text-slate-900 text-sm">ISO 45001:2018 ✓</span>
                <span className="text-xs text-slate-600">Occupational Health & Safety — Certified</span>
              </div>
              <div className="bg-green-50 p-4 border-l-4 border-green-600">
                <span className="block font-bold text-slate-900 text-sm">ISO 14001:2015 ✓</span>
                <span className="text-xs text-slate-600">Environmental Management — Certified</span>
              </div>
              <div className="bg-green-50 p-4 border-l-4 border-green-600">
                <span className="block font-bold text-slate-900 text-sm">ISO 9001:2015 ✓</span>
                <span className="text-xs text-slate-600">Quality Management — Certified</span>
              </div>
              <div className="bg-slate-50 p-4 border-l-4 border-slate-900">
                <span className="block font-bold text-slate-900 text-sm">API Q1 / Q2</span>
                <span className="text-xs text-slate-600">Specification Alignment</span>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Visual Break - Field Safety Culture */}
      <VisualBreak
        image="/assets/showcase/batch-3/safety-equipment.jpeg"
        statement="Zero Incident Culture. 185,000+ manhours without recordable injury."
        height="md"
      />

      {/* 4. FIELD SAFETY EXECUTION — With Measurable Indicators */}
      <Section id="field-safety">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <HardHat className="text-orange-600" size={32} />
            <h2 className="text-3xl font-bold text-slate-900">FIELD SAFETY EXECUTION</h2>
          </div>
          <p className="text-slate-600 max-w-4xl">
            Risk controls enforced at field level. Measurable indicators tracked per shift. Stop-work authority active for all personnel regardless of role.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <div className="group">
            <div className="h-48 overflow-hidden rounded-sm mb-4">
              <img src="/assets/hse-jsa-form.png" alt="Job Safety Analysis infographic" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            </div>
            <h3 className="font-bold text-slate-900 mb-2">Job Safety Analysis (JSA)</h3>
            <p className="text-sm text-slate-600">Task-specific hazard assessment completed before every shift and operational change. Signed by all crew.</p>
          </div>
          <div className="group">
            <div className="h-48 overflow-hidden rounded-sm mb-4">
              <img src="/assets/hse-toolbox-talk-new.png" alt="Toolbox Talk Meeting" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            </div>
            <h3 className="font-bold text-slate-900 mb-2">Toolbox Talks</h3>
            <p className="text-sm text-slate-600">Daily pre-job safety meetings. Site-specific hazards, controls, emergency procedures, lessons learned from prior ops.</p>
          </div>
          <div className="group">
            <div className="h-48 overflow-hidden rounded-sm mb-4">
              <img src="/assets/hse-equipment-inspection.jpg" alt="Equipment inspection" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            </div>
            <h3 className="font-bold text-slate-900 mb-2">Equipment Inspection</h3>
            <p className="text-sm text-slate-600">Pre-use and periodic inspections per OEM specs and API standards. Findings logged in asset integrity system.</p>
          </div>
          <div className="group">
            <div className="h-48 overflow-hidden rounded-sm mb-4">
              <img src="/assets/hse-site-supervisor.png" alt="Site Supervisor" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            </div>
            <h3 className="font-bold text-slate-900 mb-2">Site Supervision</h3>
            <p className="text-sm text-slate-600">Dedicated HSE supervisor at all operational sites. Behavior-based observations. Stop-work authority enforced.</p>
          </div>
        </div>

        {/* Measurable Enforcement Indicators */}
        <div className="bg-slate-50 border border-slate-200 p-6 rounded-sm">
          <h3 className="font-bold text-slate-900 mb-4 text-lg">Field Safety Enforcement — Measurable Indicators (Last 12 Months)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <span className="block text-3xl font-bold text-slate-900">100%</span>
              <span className="text-sm text-slate-600">JSA Compliance Rate</span>
              <span className="text-xs text-slate-500 block mt-1">No task initiated without signed JSA</span>
            </div>
            <div className="text-center">
              <span className="block text-3xl font-bold text-slate-900">12</span>
              <span className="text-sm text-slate-600">Stop-Work Authority Exercises</span>
              <span className="text-xs text-slate-500 block mt-1">All validated, all resumed after controls applied</span>
            </div>
            <div className="text-center">
              <span className="block text-3xl font-bold text-slate-900">218</span>
              <span className="text-sm text-slate-600">Safety Observations Logged</span>
              <span className="text-xs text-slate-500 block mt-1">Behavioral + condition observations tracked</span>
            </div>
            <div className="text-center">
              <span className="block text-3xl font-bold text-slate-900">100%</span>
              <span className="text-sm text-slate-600">Toolbox Talk Completion</span>
              <span className="text-xs text-slate-500 block mt-1">No shift commenced without documented TBT</span>
            </div>
          </div>
        </div>
      </Section>

      {/* 5. INCIDENT MANAGEMENT — With Post-Incident Learning */}
      <Section id="incident-management" background="light">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Activity className="text-orange-600" size={32} />
            <h2 className="text-3xl font-bold text-slate-900">INCIDENT MANAGEMENT PROCESS</h2>
          </div>
          <p className="text-slate-600 max-w-4xl">
            No-blame reporting. Every incident, near miss, and observation captured, investigated, and closed with verifiable corrective actions.
          </p>
        </div>

        {/* Workflow */}
        <div className="bg-white border border-slate-200 p-8 rounded-sm mb-8">
          <h3 className="font-bold text-slate-900 text-center mb-8 text-lg">Incident Response Workflow</h3>
          <div className="flex flex-col lg:flex-row items-center justify-center gap-0">
            <div className="bg-red-600 text-white px-4 py-3 rounded-sm text-center text-sm font-bold min-w-[140px]">Incident Occurs</div>
            <ArrowRight className="text-slate-400 hidden lg:block" size={24} />
            <ArrowRight className="text-slate-400 rotate-90 lg:hidden my-2" size={24} />
            <div className="bg-orange-600 text-white px-4 py-3 rounded-sm text-center text-sm font-bold min-w-[140px]">Immediate Notification</div>
            <ArrowRight className="text-slate-400 hidden lg:block" size={24} />
            <ArrowRight className="text-slate-400 rotate-90 lg:hidden my-2" size={24} />
            <div className="bg-slate-700 text-white px-4 py-3 rounded-sm text-center text-sm font-bold min-w-[140px]">Root Cause Analysis</div>
            <ArrowRight className="text-slate-400 hidden lg:block" size={24} />
            <ArrowRight className="text-slate-400 rotate-90 lg:hidden my-2" size={24} />
            <div className="bg-slate-800 text-white px-4 py-3 rounded-sm text-center text-sm font-bold min-w-[140px]">Corrective Action</div>
            <ArrowRight className="text-slate-400 hidden lg:block" size={24} />
            <ArrowRight className="text-slate-400 rotate-90 lg:hidden my-2" size={24} />
            <div className="bg-green-700 text-white px-4 py-3 rounded-sm text-center text-sm font-bold min-w-[140px]">Close-out & Learning</div>
          </div>
        </div>

        {/* Reporting & Escalation */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          <div className="bg-white p-6 border border-slate-200 rounded-sm">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Clock size={20} className="text-orange-600" /> Reporting Timelines
            </h3>
            <ul className="space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-3">
                <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded mt-0.5">0–1 HR</span>
                <span>Verbal notification to Site Supervisor & HSE Manager</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-orange-100 text-orange-700 text-xs font-bold px-2 py-0.5 rounded mt-0.5">4 HR</span>
                <span>Preliminary incident report submitted</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-slate-100 text-slate-700 text-xs font-bold px-2 py-0.5 rounded mt-0.5">24 HR</span>
                <span>Full written report with witness statements and photos</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-slate-100 text-slate-700 text-xs font-bold px-2 py-0.5 rounded mt-0.5">72 HR</span>
                <span>RCA completed, corrective actions assigned</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded mt-0.5">30 DAY</span>
                <span>Verification of corrective action implementation</span>
              </li>
            </ul>
          </div>
          <div className="bg-white p-6 border border-slate-200 rounded-sm">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <AlertTriangle size={20} className="text-orange-600" /> Escalation Levels
            </h3>
            <ul className="space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-3">
                <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded mt-0.5 whitespace-nowrap">LEVEL 1</span>
                <span><strong>Near Miss / Observation:</strong> Site Supervisor logs, HSE system entry</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-orange-100 text-orange-700 text-xs font-bold px-2 py-0.5 rounded mt-0.5 whitespace-nowrap">LEVEL 2</span>
                <span><strong>First Aid / Property Damage:</strong> HSE Manager notified, investigation initiated</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded mt-0.5 whitespace-nowrap">LEVEL 3</span>
                <span><strong>Medical Treatment / LTI:</strong> Executive notification, full RCA, client notification</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-red-200 text-red-800 text-xs font-bold px-2 py-0.5 rounded mt-0.5 whitespace-nowrap">LEVEL 4</span>
                <span><strong>Fatality / Major:</strong> CEO notification, regulatory reporting, crisis management</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Post-Incident Learning Integration — NEW */}
        <div className="bg-white border-2 border-orange-200 p-6 rounded-sm">
          <h3 className="font-bold text-slate-900 mb-4 text-lg">Post-Incident Learning Integration</h3>
          <p className="text-sm text-slate-600 mb-4">Every closed investigation triggers the following learning cycle — not optional, documented and tracked:</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h4 className="font-bold text-slate-900 text-sm mb-2">Lessons Learned Distribution</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Safety alert issued to all field crews</li>
                <li>• Distributed within 48 hrs of investigation closure</li>
                <li>• Acknowledgment tracked per recipient</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-slate-900 text-sm mb-2">Procedure Updates</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Affected JSAs / SOPs revised</li>
                <li>• Revision logged in document control system</li>
                <li>• Old versions archived, superseded</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-slate-900 text-sm mb-2">Training Updates</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Relevant toolbox talk material updated</li>
                <li>• Competency gap assessment triggered if applicable</li>
                <li>• Re-training records documented</li>
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* Power Statement */}
      <PowerStatement text="Every operation starts with a JSA. No exceptions. No shortcuts." variant="accent" />

      {/* 6. CERTIFICATION STATUS — Reframed */}
      <Section id="certification">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Award className="text-orange-600" size={32} />
            <h2 className="text-3xl font-bold text-slate-900">CERTIFICATION STATUS</h2>
          </div>
          <p className="text-slate-600 max-w-4xl">
            EnergiPro Solutions is a fully ISO Certified company. All three management system certifications achieved through third-party audit — Quality, Environmental, and Occupational Health & Safety.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse bg-white border border-slate-200 rounded-sm text-sm">
            <thead>
              <tr className="bg-slate-900 text-white">
                <th className="text-left px-6 py-4 font-bold">Standard</th>
                <th className="text-left px-6 py-4 font-bold">Scope</th>
                <th className="text-left px-6 py-4 font-bold">Status</th>
                <th className="text-left px-6 py-4 font-bold">Timeline</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-200">
                <td className="px-6 py-4 font-semibold text-slate-900">ISO 9001:2015</td>
                <td className="px-6 py-4 text-slate-600">Quality Management System</td>
                <td className="px-6 py-4">
                  <span className="bg-green-100 text-green-800 text-xs font-bold px-3 py-1 rounded-full">✓ Certified</span>
                </td>
                <td className="px-6 py-4 text-slate-600 text-xs">Cert No. EG1611 | Valid: Jul 2026 – Jul 2029</td>
              </tr>
              <tr className="border-b border-slate-200 bg-slate-50">
                <td className="px-6 py-4 font-semibold text-slate-900">ISO 45001:2018</td>
                <td className="px-6 py-4 text-slate-600">Occupational Health & Safety</td>
                <td className="px-6 py-4">
                  <span className="bg-green-100 text-green-800 text-xs font-bold px-3 py-1 rounded-full">✓ Certified</span>
                </td>
                <td className="px-6 py-4 text-slate-600 text-xs">Cert No. EG1613 | Valid: Jul 2026 – Jul 2029</td>
              </tr>
              <tr className="border-b border-slate-200">
                <td className="px-6 py-4 font-semibold text-slate-900">ISO 14001:2015</td>
                <td className="px-6 py-4 text-slate-600">Environmental Management</td>
                <td className="px-6 py-4">
                  <span className="bg-green-100 text-green-800 text-xs font-bold px-3 py-1 rounded-full">✓ Certified</span>
                </td>
                <td className="px-6 py-4 text-slate-600 text-xs">Cert No. EG1612 | Valid: Jul 2026 – Jul 2029</td>
              </tr>
              <tr className="border-b border-slate-200 bg-slate-50">
                <td className="px-6 py-4 font-semibold text-slate-900">API Q1 (9th Ed.)</td>
                <td className="px-6 py-4 text-slate-600">Quality — Manufacturing</td>
                <td className="px-6 py-4">
                  <span className="bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded-full">Aligned & Under Certification</span>
                </td>
                <td className="px-6 py-4 text-slate-600 text-xs">Target: Q1 2027</td>
              </tr>
              <tr className="bg-slate-50">
                <td className="px-6 py-4 font-semibold text-slate-900">API Q2 (1st Ed.)</td>
                <td className="px-6 py-4 text-slate-600">Quality — Service Supply</td>
                <td className="px-6 py-4">
                  <span className="bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded-full">Aligned & Under Certification</span>
                </td>
                <td className="px-6 py-4 text-slate-600 text-xs">Target: Q1 2027</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Download Certificates */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <a href="/files/iso-9001-certificate.pdf" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-white border border-slate-200 p-4 rounded-sm hover:border-orange-600 transition-colors group">
            <FileText className="text-orange-600 shrink-0" size={20} />
            <div>
              <span className="block text-sm font-bold text-slate-900 group-hover:text-orange-600">ISO 9001:2015 Certificate</span>
              <span className="text-xs text-slate-500">Download PDF</span>
            </div>
          </a>
          <a href="/files/iso-14001-certificate.pdf" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-white border border-slate-200 p-4 rounded-sm hover:border-orange-600 transition-colors group">
            <FileText className="text-orange-600 shrink-0" size={20} />
            <div>
              <span className="block text-sm font-bold text-slate-900 group-hover:text-orange-600">ISO 14001:2015 Certificate</span>
              <span className="text-xs text-slate-500">Download PDF</span>
            </div>
          </a>
          <a href="/files/iso-45001-certificate.pdf" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-white border border-slate-200 p-4 rounded-sm hover:border-orange-600 transition-colors group">
            <FileText className="text-orange-600 shrink-0" size={20} />
            <div>
              <span className="block text-sm font-bold text-slate-900 group-hover:text-orange-600">ISO 45001:2018 Certificate</span>
              <span className="text-xs text-slate-500">Download PDF</span>
            </div>
          </a>
        </div>

        <div className="mt-6 bg-green-50 border border-green-200 p-4 rounded-sm">
          <p className="text-sm text-slate-700">
            <strong>Status:</strong> EnergiPro Solutions is a 100% ISO Certified company. All three management system standards (ISO 9001, ISO 14001, ISO 45001) are formally certified through accredited third-party audit. Certificates, internal audit reports, and management review minutes available for client review upon request.
          </p>
        </div>
      </Section>

      {/* 7. TRAINING & COMPETENCY — Enhanced */}
      <Section id="training" background="light">
        <div className="flex flex-col lg:flex-row gap-12">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              <BookOpen className="text-orange-600" size={32} />
              <h2 className="text-3xl font-bold text-slate-900">TRAINING & COMPETENCY</h2>
            </div>
            <p className="text-slate-600 mb-6">
              No personnel deployed without validated competency. Authorization matrix controls field deployment eligibility.
            </p>

            <div className="mb-6">
              <h3 className="font-bold text-slate-900 mb-3">Role-Based Requirements</h3>
              <div className="space-y-3">
                <div className="bg-white p-4 border border-slate-200 rounded-sm">
                  <span className="font-bold text-slate-900 block text-sm">Field Operators & Technicians</span>
                  <span className="text-xs text-slate-600">H2S/Respiratory, Well Control (IADC/IWCF), First Aid/CPR, Rigging & Lifting, Confined Space</span>
                </div>
                <div className="bg-white p-4 border border-slate-200 rounded-sm">
                  <span className="font-bold text-slate-900 block text-sm">HSE Supervisors</span>
                  <span className="text-xs text-slate-600">NEBOSH/OSHA 30-hr, Incident Investigation, Permit to Work Authority, Emergency Response</span>
                </div>
                <div className="bg-white p-4 border border-slate-200 rounded-sm">
                  <span className="font-bold text-slate-900 block text-sm">Drivers & Logistics</span>
                  <span className="text-xs text-slate-600">Defensive Driving, Hazmat Transport, Journey Management, Load Securement</span>
                </div>
                <div className="bg-white p-4 border border-slate-200 rounded-sm">
                  <span className="font-bold text-slate-900 block text-sm">Management</span>
                  <span className="text-xs text-slate-600">HSE Leadership, IMS Awareness, Audit Competence, Risk Management</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1">
            {/* Competency Validation Before Deployment */}
            <div className="bg-white p-6 border border-slate-200 rounded-sm mb-6 border-t-4 border-t-orange-600">
              <h3 className="font-bold text-slate-900 mb-4">Competency Validation Before Deployment</h3>
              <ul className="space-y-3 text-sm text-slate-700">
                <li className="flex items-center gap-3"><CheckCircle size={16} className="text-green-600 flex-shrink-0" /> Competency profile verified against job requirements before mobilization</li>
                <li className="flex items-center gap-3"><CheckCircle size={16} className="text-green-600 flex-shrink-0" /> Practical assessment required for HP equipment operation authorization</li>
                <li className="flex items-center gap-3"><CheckCircle size={16} className="text-green-600 flex-shrink-0" /> No deployment if ANY required certification is expired or incomplete</li>
                <li className="flex items-center gap-3"><CheckCircle size={16} className="text-green-600 flex-shrink-0" /> Short Service Employee (SSE) mentorship for first 90 days</li>
              </ul>
            </div>

            {/* Authorization Matrix */}
            <div className="bg-white p-6 border border-slate-200 rounded-sm mb-6">
              <h3 className="font-bold text-slate-900 mb-4">Authorization Matrix</h3>
              <ul className="space-y-3 text-sm text-slate-700">
                <li className="flex items-center gap-3"><CheckCircle size={16} className="text-green-600 flex-shrink-0" /> Role-specific authorization levels (Operator, Senior Operator, Supervisor)</li>
                <li className="flex items-center gap-3"><CheckCircle size={16} className="text-green-600 flex-shrink-0" /> Equipment-specific sign-off required (choke ops, flare ignition, N₂ pumping)</li>
                <li className="flex items-center gap-3"><CheckCircle size={16} className="text-green-600 flex-shrink-0" /> Authorization verified by HSE Manager before each project assignment</li>
                <li className="flex items-center gap-3"><CheckCircle size={16} className="text-green-600 flex-shrink-0" /> Matrix updated upon certification renewal or role change</li>
              </ul>
            </div>

            {/* Re-certification Tracking */}
            <div className="bg-white p-6 border border-slate-200 rounded-sm">
              <h3 className="font-bold text-slate-900 mb-4">Re-certification Tracking</h3>
              <ul className="space-y-3 text-sm text-slate-700">
                <li className="flex items-center gap-3"><CheckCircle size={16} className="text-green-600 flex-shrink-0" /> Automated expiry alerts: 60 / 30 / 7 days advance notice</li>
                <li className="flex items-center gap-3"><CheckCircle size={16} className="text-green-600 flex-shrink-0" /> Gap analysis triggers immediate scheduling of re-training</li>
                <li className="flex items-center gap-3"><CheckCircle size={16} className="text-green-600 flex-shrink-0" /> Personnel with expired certs removed from deployment roster until renewed</li>
                <li className="flex items-center gap-3"><CheckCircle size={16} className="text-green-600 flex-shrink-0" /> Records available for client audit inspection at any time</li>
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* 8. EQUIPMENT INTEGRITY — With Outcome Statement */}
      <Section id="equipment-integrity">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Wrench className="text-orange-600" size={32} />
            <h2 className="text-3xl font-bold text-slate-900">EQUIPMENT INTEGRITY SYSTEM</h2>
          </div>
          <p className="text-slate-600 max-w-4xl">
            Every piece of pressure-rated equipment is maintained, inspected, and certified before deployment. No exceptions.
          </p>
        </div>

        {/* Outcome Statement — NEW */}
        <div className="bg-green-50 border border-green-200 p-6 rounded-sm mb-8">
          <h3 className="font-bold text-slate-900 mb-3">Equipment Integrity Outcomes (Last 12 Months)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="text-center">
              <span className="block text-3xl font-bold text-slate-900">0</span>
              <span className="text-sm text-slate-600">Equipment Failures on Location</span>
              <span className="text-xs text-slate-500 block mt-1">Zero unplanned equipment downtime</span>
            </div>
            <div className="text-center">
              <span className="block text-3xl font-bold text-slate-900">100%</span>
              <span className="text-sm text-slate-600">Pre-Mobilization Verification</span>
              <span className="text-xs text-slate-500 block mt-1">Hydro test + function test before every mob</span>
            </div>
            <div className="text-center">
              <span className="block text-3xl font-bold text-slate-900">100%</span>
              <span className="text-sm text-slate-600">Certification Currency</span>
              <span className="text-xs text-slate-500 block mt-1">No expired equipment deployed to field</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="p-6 border-t-4 border-orange-600">
            <div className="bg-orange-50 w-12 h-12 flex items-center justify-center rounded-full mb-4">
              <Clock className="text-orange-700" size={22} />
            </div>
            <h3 className="font-bold text-slate-900 mb-2">Inspection Intervals</h3>
            <ul className="text-sm text-slate-600 space-y-2">
              <li>• Daily pre-use visual inspections</li>
              <li>• Monthly functional testing</li>
              <li>• Quarterly comprehensive inspections</li>
              <li>• Annual third-party recertification</li>
              <li>• OEM-recommended schedules followed</li>
            </ul>
          </Card>
          <Card className="p-6 border-t-4 border-slate-900">
            <div className="bg-slate-100 w-12 h-12 flex items-center justify-center rounded-full mb-4">
              <Activity className="text-slate-700" size={22} />
            </div>
            <h3 className="font-bold text-slate-900 mb-2">Pressure Testing</h3>
            <ul className="text-sm text-slate-600 space-y-2">
              <li>• Hydrostatic testing per API 6A / 16C</li>
              <li>• Function test before each deployment</li>
              <li>• Pressure ratings verified and documented</li>
              <li>• Certified charts recorded and archived</li>
              <li>• Non-conforming equipment tagged out</li>
            </ul>
          </Card>
          <Card className="p-6 border-t-4 border-slate-900">
            <div className="bg-slate-100 w-12 h-12 flex items-center justify-center rounded-full mb-4">
              <FileText className="text-slate-700" size={22} />
            </div>
            <h3 className="font-bold text-slate-900 mb-2">Certification Tracking</h3>
            <ul className="text-sm text-slate-600 space-y-2">
              <li>• Unique asset ID per equipment item</li>
              <li>• Digital certification records</li>
              <li>• Third-party inspection certs archived</li>
              <li>• Color-coded tags (valid/expired/OOS)</li>
              <li>• NDT records (MPI, UT, RT) where applicable</li>
            </ul>
          </Card>
          <Card className="p-6 border-t-4 border-slate-900">
            <div className="bg-slate-100 w-12 h-12 flex items-center justify-center rounded-full mb-4">
              <ClipboardCheck className="text-slate-700" size={22} />
            </div>
            <h3 className="font-bold text-slate-900 mb-2">Maintenance Logs</h3>
            <ul className="text-sm text-slate-600 space-y-2">
              <li>• Preventive maintenance per OEM schedule</li>
              <li>• Corrective maintenance documented</li>
              <li>• Operational hours tracked per asset</li>
              <li>• Spare parts traceability maintained</li>
              <li>• Full audit trail available</li>
            </ul>
          </Card>
        </div>
      </Section>

      {/* 9. COMPLIANCE & AUDITS — ISNetworld/Avetta Prominent */}
      <Section id="compliance" background="light">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="text-orange-600" size={32} />
            <h2 className="text-3xl font-bold text-slate-900">COMPLIANCE & AUDITS</h2>
          </div>
          <p className="text-slate-600 max-w-4xl">
            Multi-layered audit program. ISNetworld / Avetta / Veriforce compatible. Full client audit support at all times.
          </p>
        </div>

        {/* ISNetworld / Avetta Compatibility — Prominent */}
        <div className="bg-white border-2 border-orange-300 p-6 rounded-sm mb-10">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Shield size={22} className="text-orange-600" />
            Contractor Management Portal Compatibility
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-slate-50 rounded-sm border border-slate-200">
              <span className="block font-bold text-slate-900 text-lg mb-1">ISNetworld</span>
              <span className="text-xs text-slate-600">Profile maintained. HSE data uploaded. Prequalification current.</span>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-sm border border-slate-200">
              <span className="block font-bold text-slate-900 text-lg mb-1">Avetta</span>
              <span className="text-xs text-slate-600">Compatible. Documentation formatted for Avetta submission requirements.</span>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-sm border border-slate-200">
              <span className="block font-bold text-slate-900 text-lg mb-1">Veriforce</span>
              <span className="text-xs text-slate-600">Compatible. Operator-driven compliance submissions supported.</span>
            </div>
          </div>
        </div>

        {/* HSE Deliverables */}
        <div className="bg-white border border-slate-200 p-8 rounded-sm mb-10">
          <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
            <ClipboardCheck size={22} className="text-orange-600" />
            HSE Deliverables & Documentation Outputs
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="flex items-start gap-3 bg-slate-50 p-3 rounded border border-slate-200">
              <FileText size={16} className="text-orange-600 shrink-0 mt-0.5" />
              <div>
                <span className="text-sm font-bold text-slate-900 block">Pressure Test Charts</span>
                <span className="text-xs text-slate-500">Hydrostatic & pneumatic per API 6A</span>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-slate-50 p-3 rounded border border-slate-200">
              <FileText size={16} className="text-orange-600 shrink-0 mt-0.5" />
              <div>
                <span className="text-sm font-bold text-slate-900 block">NDT Inspection Reports</span>
                <span className="text-xs text-slate-500">UT, MPI, DPI per ASNT SNT-TC-1A</span>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-slate-50 p-3 rounded border border-slate-200">
              <FileText size={16} className="text-orange-600 shrink-0 mt-0.5" />
              <div>
                <span className="text-sm font-bold text-slate-900 block">MTRs</span>
                <span className="text-xs text-slate-500">EN 10204 Type 3.1 / 3.2</span>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-slate-50 p-3 rounded border border-slate-200">
              <FileText size={16} className="text-orange-600 shrink-0 mt-0.5" />
              <div>
                <span className="text-sm font-bold text-slate-900 block">Equipment COC</span>
                <span className="text-xs text-slate-500">Certificate of Conformity & Inspection</span>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-slate-50 p-3 rounded border border-slate-200">
              <FileText size={16} className="text-orange-600 shrink-0 mt-0.5" />
              <div>
                <span className="text-sm font-bold text-slate-900 block">JSA & Risk Assessments</span>
                <span className="text-xs text-slate-500">Job-specific hazard analysis per task</span>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-slate-50 p-3 rounded border border-slate-200">
              <FileText size={16} className="text-orange-600 shrink-0 mt-0.5" />
              <div>
                <span className="text-sm font-bold text-slate-900 block">Incident & Near-Miss Logs</span>
                <span className="text-xs text-slate-500">Tracked with corrective action close-out</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white p-6 border border-slate-200 rounded-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-orange-100 p-2 rounded-full"><Eye size={20} className="text-orange-700" /></div>
              <h3 className="font-bold text-slate-900">Internal Audits</h3>
            </div>
            <ul className="space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Quarterly IMS compliance audits</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Monthly HSE field inspections</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> NCR tracking with root cause analysis</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> CAPA verification and close-out</li>
            </ul>
          </div>
          <div className="bg-white p-6 border border-slate-200 rounded-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-slate-100 p-2 rounded-full"><Shield size={20} className="text-slate-700" /></div>
              <h3 className="font-bold text-slate-900">Third-Party Verification</h3>
            </div>
            <ul className="space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Equipment inspection by accredited bodies</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> NDT services per ASME / API codes</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Lifting equipment load testing</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Annual IMS audit (scheduled Q3 2026)</li>
            </ul>
          </div>
          <div className="bg-white p-6 border border-slate-200 rounded-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-slate-100 p-2 rounded-full"><Users size={20} className="text-slate-700" /></div>
              <h3 className="font-bold text-slate-900">Client Audit Support</h3>
            </div>
            <ul className="space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Full support for announced/unannounced audits</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Pre-audit documentation prepared on request</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Post-audit corrective action within 7 days</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Facility access — yard, workshop, records</li>
            </ul>
          </div>
        </div>

        {/* Regulatory */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-5 border border-slate-200 text-center rounded-sm">
            <span className="block font-bold text-slate-900 mb-2 text-sm">Regulatory</span>
            <span className="text-xs text-slate-600 block">OSHA 29 CFR 1910/1926</span>
            <span className="text-xs text-slate-600 block">EPA 40 CFR / DOT 49 CFR</span>
          </div>
          <div className="bg-white p-5 border border-slate-200 text-center rounded-sm">
            <span className="block font-bold text-slate-900 mb-2 text-sm">Industry</span>
            <span className="text-xs text-slate-600 block">API RP 54 – Operations</span>
            <span className="text-xs text-slate-600 block">IADC HSE Guidelines</span>
          </div>
          <div className="bg-white p-5 border border-slate-200 text-center rounded-sm">
            <span className="block font-bold text-slate-900 mb-2 text-sm">Regional</span>
            <span className="text-xs text-slate-600 block">Saudi Aramco SAEP/SAES</span>
            <span className="text-xs text-slate-600 block">ADNOC HSE Standards</span>
          </div>
          <div className="bg-white p-5 border border-slate-200 text-center rounded-sm">
            <span className="block font-bold text-slate-900 mb-2 text-sm">Reporting</span>
            <span className="text-xs text-slate-600 block">OSHA 300 Logs</span>
            <span className="text-xs text-slate-600 block">Client HSE Portal Submissions</span>
          </div>
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section background="dark">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-4">REQUEST HSE PERFORMANCE & IMS DOCUMENTATION PACKAGE</h2>
            <p className="text-slate-300 text-lg">
              Structured package for operator and EPC vendor prequalification review.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-10">
            <div className="bg-slate-800 border border-slate-700 p-4 rounded-sm text-center">
              <FileText className="text-orange-500 mx-auto mb-2" size={24} />
              <span className="text-white text-sm font-medium block">HSE Manual</span>
            </div>
            <div className="bg-slate-800 border border-slate-700 p-4 rounded-sm text-center">
              <BarChart className="text-orange-500 mx-auto mb-2" size={24} />
              <span className="text-white text-sm font-medium block">TRIR / LTIR / EMR Data</span>
            </div>
            <div className="bg-slate-800 border border-slate-700 p-4 rounded-sm text-center">
              <ClipboardCheck className="text-orange-500 mx-auto mb-2" size={24} />
              <span className="text-white text-sm font-medium block">IMS Procedures & Audit Records</span>
            </div>
            <div className="bg-slate-800 border border-slate-700 p-4 rounded-sm text-center">
              <Eye className="text-orange-500 mx-auto mb-2" size={24} />
              <span className="text-white text-sm font-medium block">Equipment Integrity Records</span>
            </div>
            <div className="bg-slate-800 border border-slate-700 p-4 rounded-sm text-center">
              <Users className="text-orange-500 mx-auto mb-2" size={24} />
              <span className="text-white text-sm font-medium block">Training Matrix & Certs</span>
            </div>
            <div className="bg-slate-800 border border-slate-700 p-4 rounded-sm text-center">
              <Award className="text-orange-500 mx-auto mb-2" size={24} />
              <span className="text-white text-sm font-medium block">Certification Timeline</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button href="/request-quote" variant="primary" className="px-8 py-4 text-lg">
              SUBMIT RFQ
            </Button>
            <Button href="/files/energipro-capability-statement.pdf" variant="outline-light" className="px-8 py-4 text-lg">
              DOWNLOAD CAPABILITY STATEMENT
            </Button>
          </div>
        </div>
      </Section>
    </Layout>
  );
};
